import React, { useState, useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { TextField, Text, Stack } from '@bingads-webui-react/fabric-control';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { HelpButton } from '@bingads-webui-react/help-popup';
import { ScopeLevel } from '@bingads-webui-campaign-react/constants';
import { ExclusionErrors } from './exclusion-errors';
import { SiteExclusionNote } from './site-exclusions-note';

const textFieldStyles = { fieldGroup: { borderColor: '#D1D1D1' } };
const textStyles = { root: { fontWeight: 600 } };

export const SiteExclusions = ({
  level, placeholder, exclusions, readOnlyExclusions, handleExclusionInputChange, errors,
}) => {
  const i18n = useContext(I18nContext);
  const [exclusionList, setExclusionList] = useState(exclusions);

  useMemo(() => {
    setExclusionList(exclusions);
  }, [exclusions]);

  const handleExclusionTextFieldChange = useCallback(
    (input) => {
      setExclusionList(input.target.value);
      handleExclusionInputChange({ value: input.target.value, type: 'ExcludedSites' });
    },
    [setExclusionList, handleExclusionInputChange]
  );

  return (
    <div className="site-exclusions-container">
      <Stack tokens={{ childrenGap: 16 }}>
        <Stack tokens={{ childrenGap: 4 }}>
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <Text styles={textStyles}>{i18n.getString(_TL_('Website'))}</Text>
            <HelpButton
              aria-label={i18n.getString(_TL_('Click to learn more about Exclusions'))}
              topic="pop_BA_CampaignExclusionSettingsPopup_WebsitesToExclude"
            />
          </Stack>
          {!placeholder && (
            <Text styles={textStyles}>{i18n.getString(_TL_('Enter websites to exclude from showing your ads, one website per line.'))}</Text>
          )}
          <TextField
            multiline
            placeholder={placeholder}
            rows={10}
            resizable={false}
            value={exclusionList || ''}
            onChange={handleExclusionTextFieldChange}
            className="site-exclusions-textfield"
          />

          {errors && errors.length > 0 && (
            <div className="site-exclusions-errors-container">
              <ExclusionErrors errors={errors} />
            </div>
          )}
        </Stack>

        <SiteExclusionNote level={level} />

        {level === ScopeLevel.AdGroup && (
          <Stack tokens={{ childrenGap: 8 }}>
            <Text>{i18n.getString(_TL_('Campaign-level website exclusions'))}</Text>
            <TextField
              multiline
              rows={10}
              styles={textFieldStyles}
              resizable={false}
              readOnly
              value={readOnlyExclusions || ''}
              className="disabled-site-exclusions-textfield"
            />
          </Stack>
        )}
      </Stack>
    </div>
  );
};

SiteExclusions.propTypes = {
  level: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  exclusions: PropTypes.string.isRequired,
  readOnlyExclusions: PropTypes.string,
  siteMap: PropTypes.shape({}).isRequired,
  handleExclusionInputChange: PropTypes.func.isRequired,
  errors: PropTypes.shape([]),
};

SiteExclusions.defaultProps = {
  placeholder: '',
  readOnlyExclusions: '',
  errors: [],
};
